// -----------------------------------------------------------------------------
// src/components/NavBar/CreatorNavbar.js
// IMPROVED: Better formatting, spacing, and layout for profile section
// Enhanced button organization for both minimized and expanded states
// -----------------------------------------------------------------------------
import React, { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  Divider,
  IconButton,
  styled,
  ListItemIcon,
  ListItemText,
  Tooltip,
  ListItemButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LiveHelpOutlinedIcon from "@mui/icons-material/LiveHelpOutlined";
import AppsIcon from "@mui/icons-material/Apps";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import StarIcon from "@mui/icons-material/Star";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import HandshakeOutlined from "@mui/icons-material/HandshakeOutlined";
import CloseIcon from "@mui/icons-material/Close";

import { useCreatorAuth } from "../Hooks/creator-use-auth";
import ListItemLink from "./ListItemLink";
import routes from "../Config/routes";
import API from "../API";
import { drawerWidth } from "../Utils/constants";

/* —————————————————— Drawer mixins - FINAL FIX —————————————————— */
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: 80, // Fixed width for consistency (matches theme.spacing(10))
});

const IconDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "extended",
})(({ theme, extended }) => ({
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(extended
    ? {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      }
    : {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      }),
}));

/* —————————————————— Safe-area helpers —————————————————— */
const buildSafeAreaInset = (anchor, isDesktop) => {
  if (isDesktop) {
    return {}; // No safe area on desktop
  }
  // Mobile safe area
  return {
    pt: "env(safe-area-inset-top)",
    pb: "env(safe-area-inset-bottom)",
    pl: anchor === "left" ? "env(safe-area-inset-left)" : 0,
    pr: anchor === "right" ? "env(safe-area-inset-right)" : 0,
  };
};

export default function CreatorNavbar({
  isDesktop = true,
  isOpen = false, // Changed default to false (minimized by default)
  onClose,
  onToggleExtend,
  onUnreadCountChange,
  onGetMobileActions,
}) {
  const { creatorToken, logout } = useCreatorAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isExtended = !isDesktop || isOpen;

  /* —— Anchor based on screen size —— */
  const anchor = isDesktop ? "left" : "right";

  /* ——— CHAT UNREAD COUNT ——— */
  const { data: conversations = [] } = useQuery(
    ["creatorConversations", creatorToken?.creator_user?.username],
    API.conversations.fetchCreatorConversations,
    {
      enabled: !!creatorToken,
      staleTime: 1000 * 30,
      refetchOnWindowFocus: false,
    }
  );
  const unreadChatCount = useMemo(
    () => conversations.reduce((acc, c) => acc + (Number(c.unread_count) || 0), 0),
    [conversations]
  );

  /* ——— NOTIFICATION COUNTS ——— */
  const {
    data: unreadNotifCountData,
    isLoading: notifCountLoading,
    refetch: refetchUnreadNotif,
  } = useQuery(
    ["creatorNotifications", "unread_count"],
    () => API.notifications.creatorGetUnreadCount(),
    {
      enabled: !!creatorToken,
      staleTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    }
  );
  const unreadNotifCount = unreadNotifCountData?.unread_count || 0;

  const { data: unreadCollabData, refetch: refetchUnreadCollabs } = useQuery(
    ["creatorNotifications", "unread_collab_count"],
    () => API.notifications.creatorGetUnreadCollabCount(),
    {
      enabled: !!creatorToken,
      staleTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    }
  );
  const unreadCollabCount = unreadCollabData?.unread_collab_count || 0;

  /* ——— NOTIFICATION LIST / MENU ——— */
  const { data: notificationsList, refetch: refetchNotificationsList } = useQuery(
    ["creatorNotifications", "list_all"],
    () => API.notifications.creatorListAll(false),
    { enabled: false }
  );

  const markReadMutation = useMutation((id) => API.notifications.creatorMarkAsRead(id), {
    onSuccess: () => {
      refetchNotificationsList();
      refetchUnreadNotif();
      refetchUnreadCollabs();
    },
  });

  const [notifAnchor, setNotifAnchor] = useState(null);
  const notifMenuOpen = Boolean(notifAnchor);
  const handleOpenNotifications = (e) => {
    setNotifAnchor(e.currentTarget);
    refetchNotificationsList();
  };
  const handleCloseNotifications = () => setNotifAnchor(null);
  const handleNotifClick = (n) => {
    markReadMutation.mutate(n.id);
    if (n.url?.trim()) navigate(n.url);
    handleCloseNotifications();
  };

  /* ——— PROPAGATE UNREAD TO HEADER BADGE ——— */
  const totalMobileBadge = unreadChatCount + unreadNotifCount;
  useEffect(() => {
    onUnreadCountChange?.(totalMobileBadge);
  }, [totalMobileBadge, onUnreadCountChange]);

  /* ——— Mobile actions for parent ——— */
  const mobileActions = useMemo(() => [
    {
      key: 'notifications',
      icon: (
        <Badge badgeContent={notifCountLoading ? 0 : unreadNotifCount} color="secondary">
          <NotificationsNoneIcon />
        </Badge>
      ),
      onClick: handleOpenNotifications,
      tooltip: 'Notifications',
    },
    {
      key: 'settings',
      icon: <SettingsOutlinedIcon />,
      onClick: () => navigate(routes.creatorConnectEdit),
      tooltip: 'Account settings',
    },
  ], [unreadNotifCount, notifCountLoading, navigate]);

  useEffect(() => {
    if (!isDesktop && onGetMobileActions) {
      onGetMobileActions(mobileActions);
    }
  }, [isDesktop, mobileActions, onGetMobileActions]);

  /* ——— PARTNER / AGENCY LOGIC ——— */
  const agency = creatorToken?.creator_user?.agency;
  const isAgencyRestricted = (a) => a && a !== "TCC";

  /* ——— NAV LINKS ——— */
  const links = [
    { text: "Dashboard", route: routes.creatorConnectStart, icon: <DashboardIcon /> },
    { text: "Money", route: routes.creatorConnectPayoutsInvoicing, icon: <AttachMoneyIcon /> },
    {
      text: "Deals",
      route: routes.creatorCollabs,
      icon: (
        <Badge badgeContent={unreadCollabCount} color="secondary" max={999} invisible={!unreadCollabCount}>
          <HandshakeOutlined />
        </Badge>
      ),
    },
    {
      text: "Chat",
      route: routes.creatorConvos,
      icon: (
        <Badge badgeContent={unreadChatCount} color="primary" max={999} invisible={!unreadChatCount}>
          <ChatBubbleOutlineIcon />
        </Badge>
      ),
    },
    { text: "Bio", route: routes.creatorbio, icon: <AccountCircleIcon /> },
    { text: "Partner Suite", route: routes.partnerSuite, icon: <StarIcon /> },
    { text: "Apps", route: routes.creatorConnectApps, icon: <AppsIcon /> },
  ];
  const filteredLinks = links.filter(
    (item) => !((item.text === "Deals" || item.text === "Sales") && isAgencyRestricted(agency))
  );

  /* ——— BASIC PROFILE INFO ——— */
  const creator = creatorToken?.creator_user?.creator;
  const displayName = creator?.display_name || creatorToken?.creator_user?.username;
  const status = creator?.status || "";
  const photo = creator?.pfphref;
const EXTRA_HEADER_SPACE = 8; 
  /* ——— RENDER ——— */
  return (
    <>
      <IconDrawer
        anchor={anchor}
        variant={isDesktop ? "permanent" : "temporary"}
        open={isDesktop || isOpen}
        extended={isExtended}
        onClose={onClose}
        sx={{
          zIndex: (theme) => (isDesktop ? theme.zIndex.drawer : theme.zIndex.modal),
          color: "#000",
          "& .MuiDrawer-paper": {
            color: "#000",
            backgroundColor: "#FFF",
            // FINAL FIX: Proper positioning
            top: isDesktop
              ? theme.mixins.toolbar.minHeight + EXTRA_HEADER_SPACE
              : 0,
            height: isDesktop
              ? `calc(100vh - ${
                  theme.mixins.toolbar.minHeight + EXTRA_HEADER_SPACE
                }px)`
              : '100vh',
            overflowY: "auto",
            overflowX: "hidden",
            boxSizing: "border-box",
            ...buildSafeAreaInset(anchor, isDesktop),
          },
        }}
      >
        {/* IMPROVED Profile Section */}
        <Box sx={{ 
          p: isExtended ? 2 : 1.5, 
          display: "flex", 
          flexDirection: isExtended ? (isDesktop ? "row" : "column") : "column",
          alignItems: isExtended && isDesktop ? "flex-start" : "center",
          gap: isExtended ? 1.5 : 1,
          minHeight: isExtended ? 'auto' : 120,
          justifyContent: isExtended ? 'flex-start' : 'center'
        }}>
          {/* Avatar */}
          <Avatar
            src={photo}
            alt={displayName}
            sx={{ 
              width: isExtended ? 56 : 48, 
              height: isExtended ? 56 : 48,
              cursor: !isExtended ? "pointer" : "default",
              flexShrink: 0,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: !isExtended ? 'scale(1.05)' : 'none'
              }
            }}
            onClick={() => !isExtended && onToggleExtend?.()}
          />
          
          {/* Profile Info & Actions Container */}
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: isExtended && isDesktop ? 'flex-start' : 'center',
            flex: isExtended ? 1 : 'none',
            width: '100%',
            gap: isExtended ? 1 : 0.75
          }}>
            {/* Profile Text - Only show in extended mode */}
            {isExtended && (
              <Box sx={{ 
                textAlign: isDesktop ? 'left' : 'center',
                width: '100%'
              }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    lineHeight: 1.2,
                    mb: 0.25
                  }}
                >
                  {displayName}
                </Typography>
                {status && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      lineHeight: 1.2,
                      display: 'block'
                    }}
                  >
                    {status}
                  </Typography>
                )}
              </Box>
            )}
            
            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex',
              flexDirection: isExtended ? 'row' : 'column',
              alignItems: 'center',
              justifyContent: isExtended && isDesktop ? 'flex-start' : 'center',
              gap: isExtended ? 0.5 : 0.75,
              width: isExtended ? 'auto' : '100%'
            }}>
              {/* Notifications Button */}
              <Tooltip 
                title="Notifications" 
                placement={isExtended ? "bottom" : "right"}
                arrow
              >
                <IconButton 
                  onClick={handleOpenNotifications}
                  size={isExtended ? "medium" : "small"}
                  sx={{ 
                    color: 'text.primary',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <Badge 
                    badgeContent={notifCountLoading ? 0 : unreadNotifCount} 
                    color="secondary"
                    max={99}
                  >
                    <NotificationsNoneIcon 
                      fontSize={isExtended ? "medium" : "small"} 
                    />
                  </Badge>
                </IconButton>
              </Tooltip>
              
              {/* Settings Button */}
              <Tooltip 
                title="Account Settings" 
                placement={isExtended ? "bottom" : "right"}
                arrow
              >
                <IconButton 
                  onClick={() => navigate(routes.creatorConnectEdit)}
                  size={isExtended ? "medium" : "small"}
                  sx={{ 
                    color: 'text.primary',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <SettingsOutlinedIcon 
                    fontSize={isExtended ? "medium" : "small"} 
                  />
                </IconButton>
              </Tooltip>
              
              {/* Logout Button - Only show in extended desktop mode */}
              {isExtended && isDesktop && (
                <Tooltip title="Log Out" placement="bottom" arrow>
                  <IconButton
                    onClick={() => {
                      logout();
                      navigate(routes.home);
                    }}
                    size="medium"
                    sx={{ 
                      color: 'text.primary',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'error.main',
                        color: 'white',
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    <LogoutIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
        
        <Divider sx={{ mx: 1 }} />

        {/* Main Navigation */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <Box sx={{ overflowY: isExtended ? "auto" : "clip", flexGrow: 1 }}>
            <List sx={{ pt: 1 }}>
              {filteredLinks.map((item) => (
                <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
                  <ListItemLink to={item.route} primary={item.text} icon={item.icon} isOpen={isOpen} />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Bottom Section */}
          <Box>
            <Divider sx={{ mx: 1 }} />
            <List sx={{ pb: 1 }}>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => navigate("/help")}
                  sx={{
                    minHeight: 48,
                    justifyContent: isExtended ? 'initial' : 'center',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon sx={{ 
                    minWidth: isExtended ? 36 : 'auto',
                    mr: isExtended ? 3 : 0,
                    justifyContent: 'center'
                  }}>
                    <LiveHelpOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Help" 
                    sx={{ opacity: isExtended ? 1 : 0 }} 
                  />
                </ListItemButton>
              </ListItem>
              
              {/* Mobile logout */}
              {!isDesktop && (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      logout();
                      navigate(routes.home);
                    }}
                    sx={{
                      minHeight: 48,
                      justifyContent: isExtended ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: isExtended ? 36 : 'auto',
                      mr: isExtended ? 3 : 0,
                      justifyContent: 'center'
                    }}>
                      <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Log out" 
                      sx={{ opacity: isExtended ? 1 : 0 }} 
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Box>
        </Box>
      </IconDrawer>

      {/* Notification Menu */}
      <Menu
        anchorEl={notifAnchor}
        open={notifMenuOpen}
        onClose={handleCloseNotifications}
        anchorOrigin={{ vertical: "bottom", horizontal: anchor }}
        transformOrigin={{ vertical: "top", horizontal: anchor }}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 320,
            '& .MuiMenuItem-root': {
              whiteSpace: 'normal',
              padding: 2,
            }
          }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: 2,
          py: 1,
          borderBottom: '1px solid rgba(0,0,0,0.12)'
        }}>
          <Typography variant="h6">Notifications</Typography>
          <IconButton size="small" onClick={handleCloseNotifications}>
            <CloseIcon />
          </IconButton>
        </Box>

        {!notificationsList?.length && (
          <MenuItem disabled>
            <ListItemText primary="No notifications" />
          </MenuItem>
        )}

        {notificationsList?.map((n) => {
          const isUnread = !n.read_at;
          return (
            <MenuItem
              key={n.id}
              onClick={() => handleNotifClick(n)}
              sx={{
                backgroundColor: isUnread ? "#f2f2f2" : "#fff",
                borderBottom: '1px solid rgba(0,0,0,0.06)',
                '&:hover': {
                  backgroundColor: isUnread ? "#e8e8e8" : "#f5f5f5",
                }
              }}
            >
              <ListItemText
                primary={n.purpose}
                secondary={n.message}
                primaryTypographyProps={{
                  fontWeight: isUnread ? 'bold' : 'normal',
                  fontSize: '0.9rem'
                }}
                secondaryTypographyProps={{
                  fontSize: '0.8rem'
                }}
              />
              {isUnread && (
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: 'secondary.main',
                  ml: 1
                }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}